import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BrainCircuit, 
  ArrowRight, 
  Eye, 
  TrendingUp, 
  Shield, 
  Zap,
  BarChart3,
  LineChart,
  PieChart,
  Users,
  Globe,
  Lock,
  CheckCircle2,
  Sparkles
} from 'lucide-react';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <TrendingUp />,
      title: 'Portfolio Management',
      description: 'Track positions, P&L, and performance across multiple portfolios'
    },
    {
      icon: <LineChart />,
      title: 'Stock Analysis',
      description: 'Advanced charting and technical analysis tools'
    },
    {
      icon: <BrainCircuit />,
      title: 'AI Intelligence',
      description: 'AI-powered news analysis and investment insights'
    },
    {
      icon: <BarChart3 />,
      title: 'Backtesting',
      description: 'Test strategies with historical data'
    },
    {
      icon: <Shield />,
      title: 'Risk Management',
      description: 'Professional-grade risk analytics'
    },
    {
      icon: <Zap />,
      title: 'Smart Execution',
      description: 'RFQ hub and algorithmic trading'
    },
    {
      icon: <PieChart />,
      title: 'Analytics',
      description: 'Comprehensive reporting and analytics'
    },
    {
      icon: <Users />,
      title: 'Community',
      description: 'Connect with other investors'
    }
  ];

  const proFeatures = [
    'Portfolio tracking & analysis',
    'Real-time market data',
    'AI-powered news intelligence',
    'Advanced charting tools',
    'Risk management suite',
    'Backtesting engine',
    'Smart execution tools',
    'Professional analytics'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <BrainCircuit className="text-white" size={24} />
            </div>
            <span className="text-2xl font-bold text-white">AiLabQuant</span>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app?view=PUBLIC_INSIGHTS')}
              className="text-white/80 hover:text-white font-semibold px-4 py-2 transition-colors"
            >
              Market Insights
            </button>
            <button
              onClick={() => navigate('/login')}
              className="text-white/80 hover:text-white font-semibold px-4 py-2 transition-colors"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/login')}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold px-6 py-2 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              Sign Up Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full mb-6 border border-purple-500/30">
            <Sparkles size={16} />
            <span className="text-sm font-semibold">Professional Trading Platform</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Trade Smarter with
            <span className="block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              AI-Powered Insights
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 mb-10 max-w-3xl mx-auto">
            A comprehensive platform combining retail investing tools with institutional-grade analytics. 
            Portfolio management, AI intelligence, backtesting, and more.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/demo')}
              className="group flex items-center gap-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold px-8 py-4 rounded-xl transition-all shadow-2xl hover:shadow-purple-500/50 hover:scale-105"
            >
              <Eye size={20} />
              <span>Try Interactive Demo</span>
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-xl border border-white/20 hover:border-white/40 transition-all backdrop-blur-sm"
            >
              <span>Sign Up Free</span>
              <ArrowRight size={20} />
            </button>
          </div>
          
          <p className="text-slate-400 text-sm mt-6 flex items-center justify-center gap-2">
            <Lock size={14} />
            No credit card required • Full access in demo mode
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Everything You Need to Trade
            </h2>
            <p className="text-xl text-slate-400">
              40+ professional tools in one platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-purple-500/50 transition-all hover:scale-105"
              >
                <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center mb-4 text-white">
                  {React.cloneElement(feature.icon as React.ReactElement, { size: 24 })}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-12">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  Professional Tools for Every Trader
                </h2>
                <p className="text-slate-300 mb-8">
                  From beginner-friendly portfolio tracking to advanced institutional analytics, 
                  AiLabQuant provides everything you need to make informed investment decisions.
                </p>
                <button
                  onClick={() => navigate('/demo')}
                  className="flex items-center gap-2 bg-white text-purple-900 font-bold px-6 py-3 rounded-lg hover:bg-slate-100 transition-all"
                >
                  <Eye size={20} />
                  Explore in Demo Mode
                  <ArrowRight size={20} />
                </button>
              </div>
              
              <div className="space-y-3">
                {proFeatures.map((feature, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 bg-slate-900/50 backdrop-blur-sm rounded-lg p-3 border border-slate-700/50"
                  >
                    <CheckCircle2 className="text-green-400 shrink-0" size={20} />
                    <span className="text-white font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <Globe className="mx-auto text-purple-400 mb-6" size={64} />
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Trading?
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Join thousands of investors using AiLabQuant to make smarter investment decisions
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/demo')}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold px-8 py-4 rounded-xl transition-all shadow-xl"
            >
              <Eye size={20} />
              Try Demo Now
            </button>
            
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 bg-white text-purple-900 hover:bg-slate-100 font-bold px-8 py-4 rounded-xl transition-all shadow-xl"
            >
              Sign Up Free
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center">
                <BrainCircuit className="text-white" size={18} />
              </div>
              <span className="text-white font-bold">AiLabQuant</span>
            </div>
            
            <p className="text-slate-400 text-sm">
              © 2026 AiLabQuant. Professional trading platform for sophisticated investors.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

